import { Dumbbell, Layers, Play, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

export interface WorkoutListCardProps {
  title: string;
  /** Total de exercícios/itens prescritos. */
  exerciseCount: number;
  /** Rótulo "exercício" (treino) ou "item" (dieta). Default: exercício. */
  itemNoun?: "exercício" | "item";
  /** Última execução (rótulo curto: "hoje", "ontem", "12 mai"). */
  lastExecutedLabel?: string | null;
  /** Quando true, destaca como "treino de hoje" — borda accent + glow. */
  highlight?: boolean;
  /** Callback do botão "Iniciar". */
  onStart?: () => void;
  /** Loading state pro botão start. */
  starting?: boolean;
  /** Outro start em curso — desabilita o botão. */
  startDisabled?: boolean;
  /** Texto do botão de ação (default: "Iniciar treino"). */
  startLabel?: string;
}

/**
 * Card de treino/dieta na listagem. Layout: ícone à esquerda, info central,
 * botão "Iniciar" na faixa inferior. Portado de
 * `frontend/components/portal/WorkoutListCard.tsx` (Next) — classes do tema novo
 * e botão único de ação (o app novo inicia a execução direto, sem rota de detalhe).
 */
export function WorkoutListCard({
  title,
  exerciseCount,
  itemNoun = "exercício",
  lastExecutedLabel,
  highlight,
  onStart,
  starting,
  startDisabled,
  startLabel = "Iniciar treino",
}: WorkoutListCardProps) {
  const pluralNoun = itemNoun === "exercício" ? "exercícios" : "itens";

  return (
    <article
      className={cn(
        "group relative overflow-hidden rounded-2xl border bg-surface transition-all",
        highlight
          ? "border-accent-soft/45 bg-gradient-to-br from-surface to-accent/10 shadow-md"
          : "border-border-subtle hover:border-border-strong",
      )}
    >
      {highlight ? (
        <div
          aria-hidden
          className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-accent-soft opacity-15 blur-2xl"
        />
      ) : null}

      <div className="relative flex w-full items-center gap-3 p-4 text-left">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-border-subtle bg-elevated">
          <Dumbbell
            size={20}
            className={highlight ? "text-accent-soft" : "text-muted"}
            aria-hidden
          />
        </div>

        <div className="min-w-0 flex-1">
          {highlight ? (
            <p className="text-[10px] font-semibold uppercase tracking-wider text-accent-soft">
              Treino de hoje
            </p>
          ) : null}
          <h3 className="truncate font-display text-base text-ink sm:text-lg">{title}</h3>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted">
            <span className="inline-flex items-center gap-1">
              <Layers size={10} aria-hidden />
              {exerciseCount} {exerciseCount === 1 ? itemNoun : pluralNoun}
            </span>
            {lastExecutedLabel ? (
              <>
                <span className="text-faint" aria-hidden>
                  •
                </span>
                <span className="text-faint">Último: {lastExecutedLabel}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {onStart ? (
        <div className="relative border-t border-border-subtle px-4 py-3">
          <button
            type="button"
            onClick={onStart}
            disabled={starting || startDisabled}
            className={cn(
              "inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60",
              highlight
                ? "bg-accent text-ink hover:bg-accent-hover"
                : "border border-accent-soft/40 bg-accent/15 text-accent-soft hover:border-accent-soft/70 hover:bg-accent/25",
            )}
            aria-label={`Iniciar ${title}`}
          >
            {starting ? (
              <>
                <Loader2 size={15} className="animate-spin" aria-hidden />
                Iniciando…
              </>
            ) : (
              <>
                <Play size={15} aria-hidden />
                {startLabel}
              </>
            )}
          </button>
        </div>
      ) : null}
    </article>
  );
}
